import { subDays, subMonths, subYears, startOfDay } from 'date-fns';

export type DateRangeType = 'week' | 'month' | 'year' | 'custom';

export const DATE_RANGE_OPTIONS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' }
];

/**
 * Calculate date range based on type
 * Returns native Date objects for consistency
 * Uses immutable date-fns functions (no mutation)
 */
export const getDateRange = (type: DateRangeType): { start: Date; end: Date } | null => {
  const end = new Date(); // ALWAYS real current time (no timezone shift)
  
  switch (type) {
    case 'week':
      return {
        start: subDays(end, 6), // 7 days inclusive (today - 6 days)
        end
      };
      
    case 'month':
      return {
        start: subMonths(end, 1),
        end
      };
      
    case 'year':
      return {
        start: subYears(end, 1),
        end
      };
      
    default:
      return null;
  }
};

/**
 * Normalize a date to midnight (00:00:00) for consistent day comparison
 * Uses date-fns startOfDay for cleaner, safer implementation
 */
const normalizeToMidnight = (date: Date): Date => {
  return startOfDay(date);
};

/**
 * Normalize date range to ensure end is not before start
 * Central validation logic - safe comparison using getTime()
 * Normalizes to midnight to allow same-day selection
 */
export const normalizeDateRange = (
  start: Date | null,
  end: Date | null
): { start: Date | null; end: Date | null } => {
  if (!start || !end) {
    return { start, end };
  }
  
  // Normalize both dates to midnight for consistent comparison
  const normalizedStart = normalizeToMidnight(start);
  const normalizedEnd = normalizeToMidnight(end);
  
  // Prevent invalid range using timestamp comparison
  // This allows start === end (same day)
  if (normalizedEnd.getTime() < normalizedStart.getTime()) {
    return { start, end: null };
  }
  
  return { start, end };
};
